import {
  fetchAndActivate,
  getRemoteConfig,
  getValue,
  isSupported,
  RemoteConfig
} from 'firebase/remote-config';

import { isFirebaseConfigured } from '../../../config/firebase.config';
import {
  REMOTE_CONFIG_DEFAULTS,
  REMOTE_CONFIG_FETCH_INTERVAL_MS,
  REMOTE_CONFIG_REFRESH_INTERVAL_MS,
  RemoteConfigKey,
  RemoteConfigPrimitive,
  RemoteConfigValueOf
} from '../../../config/remote-config.config';
import { getFirebaseApp } from '../firebase/firebase.service';

export type RemoteConfigStatus = 'idle' | 'ready' | 'unconfigured' | 'unsupported' | 'failed';

let remoteConfigPromise: Promise<RemoteConfig | null> | null = null;
let status: RemoteConfigStatus = 'idle';
let lastError: unknown = null;

// Indexed through a widened view so the module still compiles with no keys
// registered, where `Record<never, …>` collapses to `{}`.
const defaults = REMOTE_CONFIG_DEFAULTS as Record<string, RemoteConfigPrimitive>;

const defaultOf = (key: RemoteConfigKey): RemoteConfigPrimitive => defaults[String(key)];

const ALL_KEYS = Object.keys(defaults) as unknown as RemoteConfigKey[];

const snapshots = new Map<RemoteConfigKey, RemoteConfigPrimitive>(
  ALL_KEYS.map((key) => [key, defaultOf(key)])
);
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => listener());
};

/**
 * Every error path here silently falls back to defaults, so failures are
 * invisible from the outside. Report them somewhere greppable until there is a
 * real monitoring hook to send them to.
 */
const reportFailure = (nextStatus: RemoteConfigStatus, error?: unknown) => {
  status = nextStatus;
  lastError = error || null;
  // tslint:disable-next-line: no-console
  console.warn(`[remote-config] ${nextStatus}`, error || '');
  emit();
};

const coerce = (key: RemoteConfigKey, value: ReturnType<typeof getValue> | null) => {
  const fallback = defaultOf(key);
  if (!value) {
    return fallback;
  }

  if (typeof fallback === 'boolean') {
    return value.asBoolean();
  }
  if (typeof fallback === 'number') {
    return value.asNumber();
  }
  return value.asString();
};

/**
 * Pulls every key out of the SDK into the snapshot map and notifies subscribers
 * only when something actually changed, so a poll that finds nothing new does
 * not re-render.
 */
const syncSnapshots = (remoteConfig: RemoteConfig | null) => {
  let changed = false;

  ALL_KEYS.forEach((key) => {
    let raw: ReturnType<typeof getValue> | null = null;

    if (remoteConfig) {
      try {
        raw = getValue(remoteConfig, String(key));
      } catch (error) {
        reportFailure('failed', error);
      }
    }

    const next = coerce(key, raw);
    if (snapshots.get(key) !== next) {
      snapshots.set(key, next);
      changed = true;
    }
  });

  if (changed) {
    emit();
  }
};

/**
 * Resolves once per page load. Reading N flags triggers one fetch, not N.
 */
const initRemoteConfig = (): Promise<RemoteConfig | null> => {
  if (remoteConfigPromise) {
    return remoteConfigPromise;
  }

  remoteConfigPromise = (async () => {
    try {
      if (!isFirebaseConfigured) {
        reportFailure('unconfigured');
        return null;
      }

      if (!(await isSupported())) {
        reportFailure('unsupported');
        return null;
      }

      const remoteConfig = getRemoteConfig(getFirebaseApp());
      remoteConfig.defaultConfig = REMOTE_CONFIG_DEFAULTS;
      remoteConfig.settings.minimumFetchIntervalMillis = REMOTE_CONFIG_FETCH_INTERVAL_MS;

      await fetchAndActivate(remoteConfig);
      status = 'ready';
      lastError = null;
      syncSnapshots(remoteConfig);
      emit();

      return remoteConfig;
    } catch (error) {
      reportFailure('failed', error);
      return null;
    }
  })();

  return remoteConfigPromise;
};

/**
 * Starts the fetch at boot so it overlaps with the rest of app startup, rather
 * than making whichever component reads a flag first pay the latency. Safe to
 * call more than once: the promise is memoised, so later reads await the
 * in-flight one instead of starting another.
 */
export const warmUpRemoteConfig = () => {
  void initRemoteConfig();
};

export const getRemoteConfigStatus = () => ({ status, lastError });

/**
 * Re-fetches and re-activates, then pushes any changed value to subscribers.
 * The SDK still throttles the network call to `minimumFetchIntervalMillis`.
 */
export const refreshRemoteConfig = async (): Promise<void> => {
  const remoteConfig = await initRemoteConfig();
  if (!remoteConfig) {
    return;
  }

  try {
    await fetchAndActivate(remoteConfig);
    if (status !== 'ready') {
      status = 'ready';
      lastError = null;
    }
    syncSnapshots(remoteConfig);
  } catch (error) {
    reportFailure('failed', error);
  }
};

let refreshTimer: ReturnType<typeof setInterval> | null = null;

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    void refreshRemoteConfig();
  }
};

const startPolling = () => {
  if (refreshTimer !== null) {
    return;
  }
  refreshTimer = setInterval(() => void refreshRemoteConfig(), REMOTE_CONFIG_REFRESH_INTERVAL_MS);
  document.addEventListener('visibilitychange', handleVisibilityChange);
};

const stopPolling = () => {
  if (refreshTimer === null) {
    return;
  }
  clearInterval(refreshTimer);
  refreshTimer = null;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
};

export const getRemoteSnapshot = <K extends RemoteConfigKey>(key: K): RemoteConfigValueOf<K> =>
  snapshots.get(key) as RemoteConfigValueOf<K>;

export const getRemoteDefault = <K extends RemoteConfigKey>(key: K): RemoteConfigValueOf<K> =>
  defaultOf(key) as RemoteConfigValueOf<K>;

export const subscribeRemoteConfig = (listener: () => void) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    startPolling();
  }
  void initRemoteConfig();

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stopPolling();
    }
  };
};

export const fetchRemoteValue = async <K extends RemoteConfigKey>(
  key: K
): Promise<RemoteConfigValueOf<K>> => {
  await initRemoteConfig();
  return getRemoteSnapshot(key);
};
